import { useEffect, useRef, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AnimatePresence } from 'framer-motion'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { CreativeConfiguration } from '@templates/types'
import AiCreativeIcon from 'images/ai-creative.svg'
import { DropdownContent } from './components/dropdown-content'
import * as css from './styles'
import { DEFAULT_THRESHOLD_BY_FIELD_NAME } from './utils'

interface ClassificationDto {
  categoryId: number
  threshold: number
}

interface AiClassificationsProps {
  onChangeClassifications?: (classifications: ClassificationDto[] | undefined) => void
  hasSourceUrl: boolean
  isCreativeClassificationEnabled: boolean
  creativeConfiguration: CreativeConfiguration[]
}

/**
 * This component allows users to Enable AI Content Comprehension.
 *
 * Props for the SelectAiClassifications component.
 * @typedef {Object} AiClassificationsProps
 * @property {CreativeConfiguration} [creativeConfiguration] - Creative classification object from the template.
 * @property {(classifications: Classification[]) => void} [onChangeClassifications] - Callback function for when the classifications change.
 * @property {boolean} [isCreativeClassificationEnabled] - Indicates whether AI creative classifications are enabled.
 * @property {boolean} hasSourceUrl - Indicates whether the component has a source URL.
 */

/**
 * SelectAiClassifications component for enabling AI creative classifications.
 * @param {AiClassificationsProps} props - The component's props.
 */
export const SelectAiClassifications = ({
  creativeConfiguration,
  onChangeClassifications,
  isCreativeClassificationEnabled,
  hasSourceUrl = false,
}: AiClassificationsProps) => {
  const { data: classifications = [] } = useCreativeClassifications()

  const [selected, setSelected] = useState({})
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (creativeConfiguration) {
      setSelected(
        creativeConfiguration.reduce((acc, classification) => {
          acc[classification.categoryId] = true
          return acc
        }, {}),
      )
    }
  }, [creativeConfiguration])

  const handleOutsideClick = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsDropdownOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  function getSavedThreshold(id: number) {
    return creativeConfiguration?.find(classification => classification.categoryId === id)
      ?.threshold
  }

  function getThreshold(id: number, categoryText: string) {
    const savedThreshold = getSavedThreshold(id)

    return savedThreshold || DEFAULT_THRESHOLD_BY_FIELD_NAME[categoryText]
  }

  function toggleClassification(id: number) {
    setSelected(prev => {
      const updatedClassifications = { ...prev }
      updatedClassifications[id] = !updatedClassifications[id]

      onChangeClassifications?.(
        classifications
          ?.filter(({ id }) => updatedClassifications[id])
          .map(({ id, categoryText }) => ({
            categoryId: id,
            threshold: getThreshold(id, categoryText),
          })),
      )

      return updatedClassifications
    })
  }

  const toggleContent = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  return (
    <css.Container ref={ref}>
      <css.Button isDropdownOpen={isDropdownOpen} onClick={toggleContent}>
        <css.ButtonWrapper>
          <AiCreativeIcon css={{ width: 26, height: 23, color: '#7F52FF' }} />
          <css.Title>Enable AI Content Comprehension</css.Title>
          <css.IconWrapper animate={{ rotate: isDropdownOpen ? 180 : 0 }}>
            <ExpandMoreIcon fontSize="medium" />
          </css.IconWrapper>
        </css.ButtonWrapper>
      </css.Button>
      <AnimatePresence>
        {isDropdownOpen && (
          <DropdownContent
            classifications={classifications}
            selected={selected}
            innerRef={ref}
            isCreativeClassificationEnabled={isCreativeClassificationEnabled}
            toggleClassification={(id: number) => {
              toggleClassification(id)
            }}
            hasSourceUrl={hasSourceUrl}
          />
        )}
      </AnimatePresence>
    </css.Container>
  )
}
